import { List, Map, OrderedSet, Set } from 'immutable';

import { getDefaultStatuses, getFirstNonEmptyStatus, shapeCountsForApp } from '@/utils/listings/statuses';
import { getDefaultFilters, shapeFiltersForApp, shapeFiltersForAPI, onlyComplete } from '@/utils/listings/filters';
import { getDefaultPagination, getPagination, getPaginationForAPI } from '@/utils/listings/pagination';
import { shapeId, shapeListingsForApp, shapeNumber } from '@/utils/listings/listings';
import { addPlaceholders, filterPlaceholders } from '@/utils/listings/placeholders';
import { createController } from '@/utils/controller';
import { fromBoolString } from '@/utils/bool';
import { getOrderForAPI } from '@/utils/listings/order';
import { setSelection } from '@/utils/listings/selection';
import { navigation } from '@/utils/navigation';
import { getSize } from '@/utils/iterable/getSize';
import { api } from '@/utils/api';

import { FILTER, FILTER_API_KEY, LISTING, PAGE_SIZE, PUBLISH_LIMIT, SELECTION, STATUS, STATUS_ORDER } from '@/constants/listings';
import { POLLING_INTERVAL, STATUSES, SYNC_INDICATOR } from '@/constants/shops';
import { EMPTY, FALSE, TRUE, VELA } from '@/constants';
import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { SCHEDULE_ACTION } from '@/constants/schedule';
import { ETSY, SHOPIFY } from '@/constants/channels';
import { PAGES, PARAMS } from '@/constants/routes';
import { OPERATIONS } from '@/constants/product';
import { MODALS } from '@/constants/modal';

import actions from '@/actions';

const filtersController = createController();
const listingsController = createController();
const listingIdsController = createController();
const statusesController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    filtersController,
    listingsController,
    listingIdsController,
    statusesController,
  ].forEach(abort);
}

function* addListingPlaceholder(reduction, { placeholder, shopId, status }) {
  function updatePlaceholders(placeholders = EMPTY.MAP) {
    const productId = placeholder.get(LISTING[VELA].PRODUCT_ID);

    function updateById(byId = EMPTY.MAP) {
      return byId.set(productId, placeholder);
    }

    function updateIds(ids = EMPTY.LIST) {
      return ids.push(productId);
    }

    return placeholders
      .update('byId', updateById)
      .update('ids', updateIds);
  }

  return reduction.updateIn(['listings', 'placeholders', shopId, status], updatePlaceholders);
}

function* bootstrap(reduction, { channel, db, shopId, status }) {
  abortAll();

  const listings = reduction.get('listings');
  const shop = reduction.getIn(['shops', 'byId', shopId]);

  let state = Map({
    channel,
    db,
    filters: getDefaultFilters({ channel, filters: listings.get('filters') }),
    loaded: listings.get('loaded', false),
    pagination: getDefaultPagination(),
    placeholders: listings.get('placeholders', EMPTY.MAP),
    products: Map({ processing: listings.getIn(['products', 'processing'], EMPTY.MAP) }),
    shopId,
    statuses: getDefaultStatuses({ channel, status, statuses: listings.get('statuses'), isVelaOnlyShop: shop?.get('isVelaOnlyShop') }),
  });

  if (channel === SHOPIFY && reduction.getIn(['shops', 'byId', shopId, 'tokenUpdated']) === false) {
    const domain = reduction.getIn(['shops', 'byId', shopId, 'domain']);
    state = state.set('modal', Map({ domain, type: MODALS.UPDATE_TOKEN }));
  }

  yield function effect(dispatch) {
    if (state.getIn(['statuses', 'selected']) !== status) {
      navigation().goTo({
        page: PAGES.LISTINGS,
        params: {
          [PARAMS.SHOP_ID]: shopId,
          [PARAMS.STATUS]: state.getIn(['statuses', 'selected']),
        },
      });
    }

    const loaded = state.get('loaded');
    dispatch(actions.listings.getStatuses({ loaded }));

    if (loaded) {
      const filters = EMPTY.MAP;
      dispatch(actions.listings.getFilters({ filters }));
      dispatch(actions.listings.getListings({ clear: true, filters }));
    }
  };

  return reduction.set('listings', state);
}

function* cleanUp(reduction) {
  abortAll();

  if (!getSize(reduction.get('listings'))) {
    return reduction;
  }

  const state = Map({
    loaded: reduction.getIn(['listings', 'loaded']),
    placeholders: reduction.getIn(['listings', 'placeholders']),
    products: Map({ processing: reduction.getIn(['listings', 'products', 'processing']) }),
    status: reduction.getIn(['listings', 'status']),
  });

  return reduction.set('listings', state);
}

function* copyListings(reduction, { products, source, target, optimize = false }) {
  yield function effect(dispatch) {
    dispatch(actions.listings.copyListings.pending({ products, source, target }));

    const sourceShop = reduction.getIn(['shops', 'byId', source]);
    const targetShop = reduction.getIn(['shops', 'byId', target]);
    const shopId = source;
    const db = sourceShop.get('db');
    const payload = {
      optimize,
      product_ids: products,
      source_shop: source,
      source_channel: parseInt(sourceShop.get('channelId')),
      source_db: db,
      target_shop: target,
      target_db: targetShop.get('db'),
      target_channel: parseInt(targetShop.get('channelId')),
    };

    api.products
      .copyProducts({ shopId, db, payload })
      .then(
        (response) => dispatch(actions.listings.copyListings.fulfilled({ response, target })),
        (error) => dispatch(actions.listings.copyListings.rejected({ error, target })),
      );
  };

  return reduction;
}

function* copyListingsFulfilled(reduction, { response, target }) {
  yield function effect(dispatch) {
    if (response?.result !== 'success') {
      dispatch(actions.listings.copyListings.rejected({ error: response, target }));
      return;
    }
  };

  return reduction;
}

function* copyListingsPending(reduction, { products, source, target }) {
  yield function effect(dispatch) {
    const total = getSize(products);
    const targetShop = reduction.getIn(['shops', 'byId', target]);

    dispatch(
      actions.shops.setData({
        path: ['syncData', target],
        value: Map({
          metaData: Map({ sourceShop: source }),
          modal: MODALS.SYNC.COPY,
        }),
      })
    );

    if (target === reduction.getIn(['shops', 'current'])) {
      const interval = (
        reduction.getIn(['user', 'config', 'shopsPollingIntervalShort']) ||
        POLLING_INTERVAL.SHORT
      );

      dispatch(actions.shops.rescheduleShopsPolling(interval));

      if (target === reduction.getIn(['listings', 'shopId'])) {
        navigation().goTo({
          page: PAGES.LISTINGS,
          params: {
            [PARAMS.SHOP_ID]: target,
            [PARAMS.STATUS]: STATUS[VELA].COPIED,
          },
        });
      }
    } else {
      dispatch(
        actions.notifications.add({
          count: total,
          shopId: targetShop.get('id'),
          shopName: targetShop.get('name'),
          type: NOTIFICATION.LISTINGS_COPIED,
        })
      );
    }
  };

  return reduction;
}

function* copyListingsRejected(reduction, { error, target }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.shops.setData({ path: ['syncData', target] }));

    dispatch(
      actions.notifications.add({
        message: MESSAGE.FAIL.COPY_LISTINGS,
        type: NOTIFICATION.ERROR,
      })
    );
  };

  return reduction;
}

function* deleteListings(reduction, listings) {
  yield function effect(dispatch) {
    const state = reduction.get('listings');
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');
    dispatch(actions.listings.toggleState({ listings, state: ['processing', shopId] }));
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ indicator: SYNC_INDICATOR.HIDDEN }),
      })
    );

    api.products
      .deleteProducts({ channel, db, products: listings.toArray(), shopId })
      .then(
        (response) => dispatch(actions.listings.deleteListings.fulfilled({ listings, response, shopId })),
        (error) => dispatch(actions.listings.deleteListings.rejected({ error, listings, shopId })),
      );
  };

  return reduction;
}

function* deleteListingsFulfilled(reduction, { listings, response, shopId }) {
  if (response?.result !== 'succeeded') {
    yield function effect(dispatch) {
      dispatch(actions.listings.deleteListings.rejected({ error: response?.error, listings, shopId }));
    };

    return reduction;
  }

  let state = reduction.get('listings');
  const sameShop = state.get('shopId') === shopId;

  function updateSelected(selected = EMPTY.ORDERED_SET) {
    return selected.subtract(listings);
  }

  if (sameShop && getSize(listings.intersect(state.getIn(['products', 'selected'])))) {
    state = state.set('products',
      setSelection({ products: state.get('products').update('selected', updateSelected) })
    );
  }

  yield function effect(dispatch) {
    if (sameShop) {
      dispatch(actions.listings.getStatuses());

      if (getSize(listings.intersect(Set(state.getIn(['products', 'ids']))))) {
        dispatch(actions.listings.getFilters());
        dispatch(actions.listings.getListings());
      }
    }

    if (getSize(listings.intersect(state.getIn(['products', 'processing', shopId])))) {
      dispatch(actions.listings.toggleState({ listings, state: ['processing', shopId] }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_LISTINGS,
      })
    );
  };

  return reduction.set('listings', state);
}

function* deleteListingsRejected(reduction, { error, listings, shopId }) {
  yield function effect(dispatch) {
    if (error) console.error(error);

    if (reduction.getIn(['shops', 'syncData', shopId, 'indicator']) === SYNC_INDICATOR.HIDDEN) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    if (getSize(listings.intersect(reduction.getIn(['listings', 'products', 'processing', shopId])))) {
      dispatch(actions.listings.toggleState({ listings, state: ['processing', shopId] }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_LISTINGS,
      })
    );
  };

  return reduction;
}

function* getFilters(reduction, payload) {
  if (!reduction.hasIn(['listings', 'shopId'])) return reduction;

  abort(filtersController);

  yield function effect(dispatch) {
    const { signal } = filtersController.start();
    const listings = reduction.get('listings');
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const channel = listings.get('channel');
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const status = listings.getIn(['statuses', 'selected']);
    let selected = payload?.filters || listings.getIn(['filters', 'selected']);

    if ((status === STATUS[VELA].IMPORTED || status === STATUS[VELA].UNPUBLISHED) && !selected.has(FILTER.EXISTING)) {
      selected = selected.set(FILTER.EXISTING, FALSE);
    }

    const filters = shapeFiltersForAPI({ channel, filters: selected, status, userFeatures });

    api.listings
      .getFilters({ channel, db, filters, shopId, signal })
      .then(
        (response) => dispatch(actions.listings.getFilters.fulfilled({ response, selected, shopId })),
        (error) => dispatch(actions.listings.getFilters.rejected({ error, signal })),
      );
  };

  return reduction.setIn(['listings', 'filters', 'loading'], true);
}

function* getFiltersFulfilled(reduction, { response, selected, shopId }) {
  abort(filtersController);

  const listings = reduction.get('listings');
  const channel = listings.get('channel');

  if (!channel) return reduction;

  if (selected && selected.has(FILTER.EXISTING) && !listings.hasIn(['filters', 'values', FILTER.EXISTING])) {
    const current = selected.get(FILTER.EXISTING);
    const opposite = String(!fromBoolString(current));
    const counts = response[FILTER_API_KEY[FILTER.EXISTING]];

    if (shapeNumber(counts[current]) < shapeNumber(counts[opposite])) {
      yield function effect(dispatch) {
        dispatch(actions.listings.setFilter({ filter: FILTER.EXISTING, value: opposite }));
      };

      return reduction;
    }
  }

  const status = listings.getIn(['statuses', 'selected']);
  const filters = listings.get('filters').delete('loading').set('selected', selected);
  const shop = reduction.getIn(['shops', 'byId', shopId]);
  const shopData = reduction.getIn(['data', 'shopsData', shopId], EMPTY.MAP);
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);

  return reduction.setIn(['listings', 'filters'],
    shapeFiltersForApp({
      channel,
      filters,
      response,
      shop,
      shopData,
      status,
      userFeatures,
    })
  );
}

function* getFiltersRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(filtersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['listings', 'filters', 'loading']);
}

function* getListings(reduction, payload = {}) {
  if (!reduction.hasIn(['listings', 'shopId'])) return reduction;

  abort(listingsController);

  const { actions: actionsToDispatch, clear, filters: selected } = payload;

  if (clear) {
    abort(listingIdsController);
  }

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const listings = reduction.get('listings');
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const channel = listings.get('channel');
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const status = listings.getIn(['statuses', 'selected']);
    const order = {
      by: listings.getIn(['order', 'by']),
      type: listings.getIn(['order', 'type']),
    };

    if (
      order.by &&
      status !== listings.get('status') &&
      !STATUS_ORDER[channel][status].has(order.by)
    ) {
      delete order.by;
      delete order.type;
    }

    const page = (
      clear ||
      order.by !== listings.getIn(['products', 'order', 'by']) ||
      order.type !== listings.getIn(['products', 'order', 'type'])
    )
      ? 0
      : listings.getIn(['pagination', 'currentPage']);

    let selectedFilters = selected || listings.getIn(['filters', 'selected']);

    if ((status === STATUS[VELA].IMPORTED || status === STATUS[VELA].UNPUBLISHED) && !selectedFilters.has(FILTER.EXISTING)) {
      selectedFilters = selectedFilters.set(FILTER.EXISTING, FALSE);
    }

    const filters = {
      ...shapeFiltersForAPI({ channel, filters: selectedFilters, status, userFeatures }),
      ...getOrderForAPI({ channel, order, status }),
      ...getPaginationForAPI(page),
    };

    api.listings
      .getListings({ channel, db, filters, shopId, signal })
      .then(
        (response) => {
          dispatch(
            actions.listings.getListings.fulfilled({
              actions: actionsToDispatch,
              clear,
              order,
              page,
              response,
              status,
            })
          );
        },
        (error) => dispatch(actions.listings.getListings.rejected({ error, signal })),
      );
  };

  return reduction.getIn(['listings', 'products', 'loading'])
    ? reduction
    : reduction.setIn(['listings', 'products', 'loading'], true);
}

function* getListingsFulfilled(reduction, { actions: actionsToDispatch, clear, order, page, response, status }) {
  abort(listingsController);
  let state = reduction.get('listings');
  const channel = state.get('channel');

  if (!channel) return reduction;

  state = state.deleteIn(['products', 'loading']);

  if (clear) {
    state = state
      .deleteIn(['products', 'all'])
      .deleteIn(['products', 'oldFilters'])
      .deleteIn(['products', 'selection'])
      .setIn(['products', 'selected'], EMPTY.ORDERED_SET);
  }

  if (status !== state.get('status')) {
    state = state.set('status', status);
  }

  if (!order.by) {
    state = state.delete('order').deleteIn(['products', 'order']);
  } else {
    if (
      order.by !== state.getIn(['order', 'by']) ||
      order.type !== state.getIn(['order', 'type'])
    ) {
      state = state.set('order', Map(order));
    }

    if (
      state.getIn(['order', 'by']) !== state.getIn(['products', 'order', 'by']) ||
      state.getIn(['order', 'type']) !== state.getIn(['products', 'order', 'type'])
    ) {
      state = state.setIn(['products', 'order'], state.get('order'));
    }
  }

  const total = shapeNumber(response.total);

  state = state.setIn(['products', 'total'], total);

  if (!total) {
    state = state
      .deleteIn(['products', 'all'])
      .deleteIn(['products', 'byId'])
      .deleteIn(['products', 'ids'])
      .deleteIn(['products', 'oldFilters'])
      .deleteIn(['products', 'selection'])
      .setIn(['products', 'selected'], EMPTY.ORDERED_SET)
      .set('pagination', getDefaultPagination());

    return reduction.set('listings', state);
  } else if (!getSize(response.listings) && page > 0) {
    yield function effect(dispatch) {
      dispatch(actions.listings.setPage(Math.max(0, Math.floor((total - 1) / PAGE_SIZE))));
    };

    return reduction.set('listings', state.setIn(['products', 'loading']));
  }

  const shopId = state.get('shopId');
  const userShops = reduction.get('shops');
  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  const shopData = reduction.getIn(['data', 'shopsData', shopId]);
  const { byId, ids } = shapeListingsForApp({ channel, response, shopData, userFeatures, userShops });

  state = state
    .set('pagination', getPagination({ page, total }))
    .set('products',
      setSelection({ products: state.get('products').set('byId', byId).set('ids', ids) })
    );

  if (state.hasIn(['placeholders', shopId, status]) || state.hasIn(['products', 'processing', shopId])) {
    if (
      !reduction.hasIn(['shops', 'syncData', shopId]) ||
      reduction.getIn(['shops', 'syncData', shopId, 'complete'])
    ) {
      state = state
        .deleteIn(['placeholders', shopId])
        .deleteIn(['products', 'processing', shopId]);
    } else if (state.hasIn(['placeholders', shopId, status])) {
      const placeholders = filterPlaceholders({ shopId, state, status });

      if (!getSize(placeholders.get('ids'))) {
        state = state.deleteIn(['placeholders', shopId, status]);

        if (!getSize(state.getIn(['placeholders', shopId]))) {
          state = state.deleteIn(['placeholders', shopId]);
        }
      } else {
        state = addPlaceholders({ placeholders, state, status });
      }
    }
  }

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return reduction.set('listings', state);
}

function* getListingsRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return reduction.deleteIn(['listings', 'products', 'loading']);
}

function* getStatuses(reduction, payload = {}) {
  if (!reduction.hasIn(['listings', 'shopId'])) return reduction;

  abort(statusesController);

  yield function effect(dispatch) {
    const { signal } = statusesController.start();
    const listings = reduction.get('listings');
    const channel = listings.get('channel');
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const { loaded = true } = payload;

    api.listings
      .getStatuses({ channel, db, shopId, signal })
      .then(
        (response) => dispatch(actions.listings.getStatuses.fulfilled({ loaded, response })),
        (error) => dispatch(actions.listings.getStatuses.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getStatusesFulfilled(reduction, { loaded, response }) {
  abort(statusesController);

  const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
  let state = reduction.get('listings');

  const channel = state.get('channel');

  if (!channel) return reduction;

  const counts = shapeCountsForApp({ channel, response, userFeatures });
  let statuses = state.get('statuses').set('counts', counts);

  function getEmptyState() {
    return state
      .delete('order')
      .set('filters', getDefaultFilters({ channel }).set('options', EMPTY.LIST))
      .set('pagination', getDefaultPagination())
      .set('products', Map({ processing: state.getIn(['products', 'processing']), total: 0 }));
  }

  if (!loaded) {
    if (!statuses.getIn(['counts', statuses.get('selected')])) {
      const firstNonEmpty = getFirstNonEmptyStatus({ channel, counts });

      if (firstNonEmpty) {
        statuses = statuses.set('selected', firstNonEmpty);
      } else {
        state = getEmptyState().set('status', statuses.get('selected'));
      }
    }

    state = state.set('loaded', true);
  }

  yield function effect(dispatch) {
    if (statuses.get('selected') !== state.getIn(['statuses', 'selected'])) {
      navigation().goTo({
        page: PAGES.LISTINGS,
        params: {
          [PARAMS.SHOP_ID]: state.get('shopId'),
          [PARAMS.STATUS]: statuses.get('selected'),
        },
      });
    }

    if (!loaded) {
      const filters = EMPTY.MAP;
      dispatch(actions.listings.getFilters({ filters }));
      dispatch(actions.listings.getListings({ clear: true, filters }));
    }
  };

  return reduction.set('listings', state.set('statuses', statuses));
}

function* getStatusesRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(statusesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* publishListings(reduction, { bulk, confirmed, override, placeholder, products, status }) {
  const listings = reduction.get('listings');
  const channel = listings.get('channel');
  const db = listings.get('db');
  const shopId = listings.get('shopId');

  if (!confirmed) {
    if (override) {
      yield function effect(dispatch) {
        dispatch(
          actions.listings.setModal({
            bulk,
            placeholder,
            products,
            type: MODALS.CONFIRMATIONS.UPDATE_EXISTING,
          })
        );
      };

      return reduction;
    } else if (channel === ETSY && status === STATUS[ETSY].ACTIVE) {
      yield function effect(dispatch) {
        dispatch(
          actions.listings.setModal({
            action: actions.listings.publishListings({
              bulk,
              confirmed: true,
              placeholder,
              products,
              status,
            }),
            type: MODALS.CONFIRMATIONS.PUBLISH,
          })
        );
      };

      return reduction;
    }
  }

  yield function effect(dispatch) {
    dispatch(actions.listings.publishListings.pending({ bulk, placeholder, shopId, status }));

    if (override) {
      api.products
        .bulkOverride({ db, listings: products, shopId })
        .then(
          () => dispatch(actions.listings.publishListings.fulfilled({ shopId })),
          (error) => dispatch(actions.listings.publishListings.rejected({ error, shopId })),
        );
    } else {
      const operations = [{ type: OPERATIONS[channel].STATUS, products, value: status }];
      const params = {};

      if (bulk) {
        params.initial_sync_status = STATUSES.PUBLISHING_COPY;
      }

      api.products
        .editSingleProduct({ channel, db, operations, params, shopId, status })
        .then(
          () => dispatch(actions.listings.publishListings.fulfilled({ shopId, status })),
          (error) => dispatch(actions.listings.publishListings.rejected({ error, shopId })),
        );
    }
  };

  return reduction;
}

function* publishListingsFulfilled(reduction, { shopId, status }) {
  if (shopId === reduction.getIn(['listings', 'shopId'])) {
    yield function effect(dispatch) {
      dispatch(actions.listings.getStatuses());
    };

    if (status && status === reduction.getIn(['listings', 'statuses', 'selected'])) {
      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters());
        dispatch(actions.listings.getListings({ clear: true }));
      };

      return reduction.setIn(['listings', 'products', 'loading'], 'scroll');
    }
  }

  return reduction;
}

function* publishListingsPending(reduction, { bulk, placeholder, shopId, status }) {
  yield function effect(dispatch) {
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: bulk
          ? Map({ modal: MODALS.SYNC.PUBLISH })
          : Map({ indicator: SYNC_INDICATOR.PUBLISH }),
      })
    );

    if (status) {
      if (placeholder) {
        dispatch(actions.listings.addListingPlaceholder({ placeholder, shopId, status }));
      }

      if (shopId === reduction.getIn(['listings', 'shopId'])) {
        navigation().goTo({ page: PAGES.LISTINGS, params: { [PARAMS.SHOP_ID]: shopId, [PARAMS.STATUS]: status }});
      }
    }
  };

  return reduction;
}

function* publishListingsRejected(reduction, { error, shopId }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.PUBLISH_LISTINGS,
      })
    );
  };

  return reduction;
}

function* schedulePublish(reduction, { products, scheduledAt, value }) {
  yield function effect(dispatch) {
    if (value === undefined) {
      if (scheduledAt) {
        dispatch(actions.listings.setModal({ products, scheduledAt, type: MODALS.RESCHEDULE_PUBLISH }));
      } else {
        dispatch(actions.listings.setModal({ products, type: MODALS.SCHEDULE_PUBLISH }));
      }

      return;
    }

    const state = reduction.get('listings');
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');
    const status = state.get('status');
    const operations = [{ products: products.toArray(), type: OPERATIONS[channel].SCHEDULED_AT, value }];
    const action = value
      ? scheduledAt
        ? SCHEDULE_ACTION.RESCHEDULE
        : SCHEDULE_ACTION.SCHEDULE
      : SCHEDULE_ACTION.CANCEL;

    dispatch(actions.listings.toggleState({ listings: products, state: ['processing', shopId] }));
    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ indicator: SYNC_INDICATOR.HIDDEN }),
      })
    );

    api.products
      .bulkEditProducts({ channel, db, operations, shopId, status })
      .then(
        (response) => dispatch(actions.listings.schedulePublish.fulfilled({ action, products, response, shopId })),
        (error) => dispatch(actions.listings.schedulePublish.rejected({ action, error, products, shopId })),
      );
  };

  return reduction;
}

function* schedulePublishFulfilled(reduction, { action, products, response, shopId }) {
  if (response?.result !== 'succeeded') {
    yield function effect(dispatch) {
      dispatch(actions.listings.schedulePublish.rejected({ action, error: response?.error, products, shopId }));
    };

    return reduction;
  }

  yield function effect(dispatch) {
    const actionMessage = {
      [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.SUCCESS.RESCHEDULE_PUBLICATION,
      [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.SUCCESS.SCHEDULE_PUBLICATION,
      [SCHEDULE_ACTION.CANCEL]: MESSAGE.SUCCESS.CANCEL_PUBLICATION,
    };

    dispatch(actions.notifications.add({ type: NOTIFICATION.SUCCESS, message: actionMessage[action] }));
  };

  function updateSelected(selected = EMPTY.ORDERED_SET) {
    return selected.subtract(products);
  }

  if (
    reduction.getIn(['listings', 'shopId']) === shopId &&
    getSize(products.intersect(reduction.getIn(['listings', 'products', 'selected']))) > 0
  ) {
    return reduction.setIn(['listings', 'products'],
      setSelection({ products: reduction.getIn(['listings', 'products']).update('selected', updateSelected) })
    );
  }

  return reduction;
}

function* schedulePublishRejected(reduction, { action, error, products, shopId }) {
  yield function effect(dispatch) {
    if (error) console.error(error);

    if (reduction.getIn(['shops', 'syncData', shopId, 'indicator']) === SYNC_INDICATOR.HIDDEN) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    if (getSize(products.intersect(reduction.getIn(['listings', 'products', 'processing', shopId]))) > 0) {
      dispatch(actions.listings.toggleState({ listings: products, state: ['processing', shopId] }));
    }

    const actionMessage = {
      [SCHEDULE_ACTION.RESCHEDULE]: MESSAGE.FAIL.RESCHEDULE_PUBLICATION,
      [SCHEDULE_ACTION.SCHEDULE]: MESSAGE.FAIL.SCHEDULE_PUBLICATION,
      [SCHEDULE_ACTION.CANCEL]: MESSAGE.FAIL.CANCEL_PUBLICATION,
    };

    dispatch(actions.notifications.add({ type: NOTIFICATION.ERROR, message: actionMessage[action] }));
  };

  return reduction;
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['listings', ...path]
    : ['listings', path];

  if (value !== undefined) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

function* setFilter(reduction, { filter, value }) {
  let filters = reduction.getIn(['listings', 'filters', 'selected']);

  function updateFilter(values = EMPTY.SET) {
    if (Set.isSet(value)) {
      if (getSize(values.intersect(value))) {
        return values.subtract(value);
      } else {
        return values.union(value);
      }
    } else {
      if (values.has(value)) {
        return values.delete(value);
      } else {
        return values.add(value);
      }
    }
  }

  switch (filter) {
    case FILTER.EXISTING: {
      filters = filters.set(filter, value);

      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters({ filters }));
        dispatch(actions.listings.getListings({ clear: true, filters }));
      };

      return reduction
        .setIn(['listings', 'filters', 'selected'], filters)
        .setIn(['listings', 'products', 'loading'], 'scroll');
    }

    case FILTER.PROFILE_TYPE: {
      filters = filters.set(filter, value);

      if (getSize(filters.get(FILTER.PROFILE))) {
        filters = filters.delete(FILTER.PROFILE);

        yield function effect(dispatch) {
          dispatch(actions.listings.getFilters({ filters }));
          dispatch(actions.listings.getListings({ clear: true, filters }));
        };

        return reduction
          .setIn(['listings', 'filters', 'selected'], filters)
          .setIn(['listings', 'products', 'loading'], 'scroll');
      }

      return reduction.setIn(['listings', 'filters', 'selected'], filters);
    }

    case FILTER.SCORE: {
      filters = getSize(value) > 0
        ? filters.set(filter, value)
        : filters.delete(filter);

      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters({ filters }));
        dispatch(actions.listings.getListings({ clear: true, filters }));
      };

      return reduction
        .setIn(['listings', 'filters', 'loading'], true)
        .setIn(['listings', 'filters', 'selected'], filters)
        .setIn(['listings', 'products', 'loading'], 'scroll');
    }

    case FILTER.TITLE: {
      filters = value
        ? filters.set(filter, value)
        : filters.delete(filter);

      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters({ filters }));
        dispatch(actions.listings.getListings({ clear: true, filters }));
      };

      return reduction
        .setIn(['listings', 'filters', 'loading'], true)
        .setIn(['listings', 'filters', 'selected'], filters)
        .setIn(['listings', 'products', 'loading'], 'scroll');
    }

    default: {
      filters = filters.update(filter, updateFilter);

      if (getSize(filters.get(filter)) === 0) {
        filters = filters.delete(filter);
      }

      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters({ filters }));
        dispatch(actions.listings.getListings({ clear: true, filters }));
      };

      return reduction
        .setIn(['listings', 'filters', 'selected'], filters)
        .setIn(['listings', 'products', 'loading'], 'scroll');
    }
  }
}

function* setModal(reduction, payload) {
  return payload
    ? reduction.setIn(['listings', 'modal'], Map(payload))
    : reduction.deleteIn(['listings', 'modal']);
}

function* setOrder(reduction, { by, type }) {
  yield function effect(dispatch) {
    dispatch(actions.listings.getListings({ clear: true }));
  };

  return reduction
    .setIn(['listings', 'products', 'loading'], 'scroll')
    .setIn(['listings', 'order'], Map({ by, type }));
}

function* setPage(reduction, page) {
  yield function effect(dispatch) {
    dispatch(actions.listings.getListings());
  };

  return reduction
    .setIn(['listings', 'pagination', 'currentPage'], page)
    .setIn(['listings', 'products', 'loading'], 'scroll');
}

function* setStatus(reduction, status) {
  abort(filtersController);
  abort(listingsController);
  abort(listingIdsController);

  let listings = reduction.get('listings');
  const statuses = listings.get('statuses');

  if (status === statuses.get('selected')) return reduction;

  if (!statuses.getIn(['counts', status])) {
    const channel = listings.get('channel');

    listings = listings
      .set('filters', getDefaultFilters({ channel }).set('options', EMPTY.LIST))
      .set('pagination', getDefaultPagination())
      .set('products', Map({ processing: listings.getIn(['products', 'processing']), total: 0 }))
      .set('status', status)
      .set('statuses', statuses.set('selected', status));

    const order = listings.get('order');

    if (order && STATUS_ORDER[channel][status].has(order.get('by'))) {
      listings = listings.setIn(['products', 'order'], order);
    } else {
      listings = listings.delete('order');
    }

    return reduction.set('listings', listings);
  }

  let filters = EMPTY.MAP;
  const title = listings.getIn(['filters', 'selected', 'title']);

  if (title) {
    filters = filters.set(FILTER.TITLE, title);
  }

  yield function effect(dispatch) {
    dispatch(actions.listings.getFilters({ filters }));
    dispatch(actions.listings.getListings({ clear: true, filters }));
  };

  return reduction
    .setIn(['listings', 'products', 'loading'], 'scroll')
    .setIn(['listings', 'statuses'], statuses.set('selected', status));
}

function* toggleListings(reduction, { limit, listing, oldFilters, selection }) {
  abort(listingIdsController);

  let products = reduction.getIn(['listings', 'products']);

  if (listing) {
    products = products.update('selected', function updateSelected(selected = EMPTY.ORDERED_SET) {
      return selected.has(listing)
        ? selected.delete(listing)
        : selected.add(listing);
    });

    return reduction.setIn(['listings', 'products'], setSelection({ products }));
  }

  if (limit) {
    const { signal } = listingIdsController.start();
    const listings = reduction.get('listings');
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const channel = listings.get('channel');
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const filters = listings.getIn(['filters', 'selected']);
    const status = listings.getIn(['statuses', 'selected']);
    const order = {
      by: listings.getIn(['order', 'by']),
      type: listings.getIn(['order', 'type']),
    };

    const params = {
      ...shapeFiltersForAPI({ channel, filters, status, userFeatures }),
      ...getOrderForAPI({ channel, order, status }),
      limit,
      offset: 0,
    };

    yield function effect(dispatch) {
      api.listings
        .getListings({ channel, db, filters: params, shopId, signal })
        .then(
          (response) => dispatch(actions.listings.toggleListings.fulfilled({ channel, limit, response })),
          (error) => dispatch(actions.listings.toggleListings.rejected({ error, signal })),
        );
    };

    return reduction.setIn(['listings', 'products', 'loading'], true);
  }

  if (selection === SELECTION.COMPLETE) {
    let listings = reduction.get('listings');
    let filters = listings.getIn(['filters', 'selected']);

    if (!onlyComplete(filters)) {
      filters = filters.set(FILTER.IS_COMPLETE, Set([TRUE]));
      listings = listings
        .setIn(['filters', 'selected'], filters)
        .setIn(['filters', 'loading'], true)
        .setIn(['products', 'loading'], true);

      const actionsToDispatch = [
        actions.listings.toggleListings({
          oldFilters: reduction.getIn(['listings', 'filters', 'selected']),
          selection: SELECTION.COMPLETE,
        }),
      ];

      yield function effect(dispatch) {
        dispatch(actions.listings.getFilters({ filters }));
        dispatch(actions.listings.getListings({ actions: actionsToDispatch, clear: true, filters }));
      };

      return reduction.set('listings', listings);
    }

    const { signal } = listingIdsController.start();
    const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
    const channel = listings.get('channel');
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const status = listings.getIn(['statuses', 'selected']);
    const order = {
      by: listings.getIn(['order', 'by']),
      type: listings.getIn(['order', 'type']),
    };

    const params = {
      ...shapeFiltersForAPI({ channel, filters, status, userFeatures }),
      ...getOrderForAPI({ channel, order, status }),
      limit: PUBLISH_LIMIT,
      offset: 0,
    };

    yield function effect(dispatch) {
      api.listings
        .getListings({ channel, db, filters: params, shopId, signal })
        .then(
          (response) => {
            dispatch(actions.listings.toggleListings.fulfilled({ channel, oldFilters, response, selection }));
          },
          (error) => dispatch(actions.listings.toggleListings.rejected({ error, signal })),
        );
    };

    return reduction.setIn(['listings', 'products', 'loading'], true);
  }

  if (selection === SELECTION.NONE && reduction.hasIn(['listings', 'products', 'oldFilters'])) {
    const filters = reduction.getIn(['listings', 'products', 'oldFilters']);

    yield function effect(dispatch) {
      dispatch(actions.listings.getFilters({ filters }));
      dispatch(actions.listings.getListings({ clear: true, filters }));
    };

    return reduction
      .deleteIn(['listings', 'products', 'oldFilters'])
      .setIn(['listings', 'filters', 'selected'], filters);
  }

  if (selection === SELECTION.NONE) {
    products = products.set('selected', EMPTY.SET);
    return reduction.setIn(['listings', 'products'], setSelection({ products }));
  }

  if (
    selection === SELECTION.PAGE || (
      selection === SELECTION.ALL &&
      getSize(products.get('ids')) === products.get('total')
    )
  ) {
    products = products.update('selected', function updateSelected(selected = EMPTY.ORDERED_SET) {
      return products.get('total') > PAGE_SIZE && products.get('selection') === SELECTION.ALL
        ? OrderedSet(products.get('ids'))
        : products.get('selection')
          ? selected.subtract(OrderedSet(products.get('ids')))
          : selected.union(OrderedSet(products.get('ids')));
    });

    return reduction.setIn(['listings', 'products'], setSelection({ products }));
  }

  if (selection === SELECTION.ALL) {
    if (products.has('all')) {
      products = products.set('selected', products.get('all').toOrderedSet());
      return reduction.setIn(['listings', 'products'], setSelection({ products }));
    }

    yield function effect(dispatch) {
      const { signal } = listingIdsController.start();
      const listings = reduction.get('listings');
      const userFeatures = reduction.getIn(['user', 'features'], EMPTY.MAP);
      const channel = listings.get('channel');
      const db = listings.get('db');
      const shopId = listings.get('shopId');
      const filters = listings.getIn(['filters', 'selected']);
      const status = listings.getIn(['statuses', 'selected']);
      const order = {
        by: listings.getIn(['order', 'by']),
        type: listings.getIn(['order', 'type']),
      };

      const payload = {
        ...shapeFiltersForAPI({ channel, filters, status, userFeatures }),
        ...getOrderForAPI({ channel, order, status }),
      };

      api.listings
        .getListingIds({ channel, db, payload, shopId, signal })
        .then(
          (response) => dispatch(actions.listings.toggleListings.fulfilled({ response, selection })),
          (error) => dispatch(actions.listings.toggleListings.rejected({ error, signal })),
        );
    };

    return reduction.setIn(['listings', 'products', 'loading'], true);
  }

  return reduction;
}

function* toggleListingsFulfilled(reduction, { channel, limit, oldFilters, response, selection }) {
  abort(listingIdsController);

  function mapListings(listing) {
    return shapeId(shapeId(listing[LISTING[channel].PRODUCT_ID]));
  }

  switch (selection) {
    case SELECTION.ALL: {
      const ids = response.map(shapeId);
      const products = reduction
        .getIn(['listings', 'products'])
        .delete('loading')
        .set('all', List(ids))
        .set('selected', OrderedSet(ids));

      return reduction.setIn(['listings', 'products'], setSelection({ products }));
    }

    case SELECTION.COMPLETE: {
      const ids = response.listings.map(mapListings);
      let products = reduction
        .getIn(['listings', 'products'])
        .delete('loading')
        .set('selected', OrderedSet(ids));

      if (oldFilters) {
        products = products.set('oldFilters', oldFilters);
      }

      return reduction.setIn(['listings', 'products'], setSelection({ products }));
    }

    default: {
      if (limit) {
        const ids = response.listings.map(mapListings);
        const products = reduction
          .getIn(['listings', 'products'])
          .delete('loading')
          .set('selected', OrderedSet(ids));

        return reduction.setIn(['listings', 'products'], setSelection({ products }));
      }

      return reduction;
    }
  }
}

function* toggleListingsRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(listingIdsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['listings', 'products', 'loading']);
}

function* toggleSidebarGroup(reduction, { key, value }) {
  function updateExpanded(expanded) {
    return expanded.has(value)
      ? expanded.delete(value)
      : expanded.add(value);
  }

  return reduction.updateIn(['listings', key, 'expanded'], updateExpanded);
}

function* toggleState(reduction, { listings, state: path }) {
  function reduceListingIds(result, id) {
    return result.has(id)
      ? result.delete(id)
      : result.add(id);
  }

  const fullPath = Array.isArray(path)
    ? ['listings', 'products', ...path]
    : ['listings', 'products', path];

  const state = listings.reduce(reduceListingIds, reduction.getIn(fullPath, EMPTY.SET));

  return getSize(state)
    ? reduction.setIn(fullPath, state)
    : reduction.deleteIn(fullPath);
}

const listings = {
  [actions.listings.addListingPlaceholder]: addListingPlaceholder,
  [actions.listings.bootstrap]: bootstrap,
  [actions.listings.cleanUp]: cleanUp,
  [actions.listings.copyListings]: copyListings,
  [actions.listings.copyListings.fulfilled]: copyListingsFulfilled,
  [actions.listings.copyListings.pending]: copyListingsPending,
  [actions.listings.copyListings.rejected]: copyListingsRejected,
  [actions.listings.deleteListings]: deleteListings,
  [actions.listings.deleteListings.fulfilled]: deleteListingsFulfilled,
  [actions.listings.deleteListings.rejected]: deleteListingsRejected,
  [actions.listings.getFilters]: getFilters,
  [actions.listings.getFilters.fulfilled]: getFiltersFulfilled,
  [actions.listings.getFilters.rejected]: getFiltersRejected,
  [actions.listings.getListings]: getListings,
  [actions.listings.getListings.fulfilled]: getListingsFulfilled,
  [actions.listings.getListings.rejected]: getListingsRejected,
  [actions.listings.getStatuses]: getStatuses,
  [actions.listings.getStatuses.fulfilled]: getStatusesFulfilled,
  [actions.listings.getStatuses.rejected]: getStatusesRejected,
  [actions.listings.publishListings]: publishListings,
  [actions.listings.publishListings.fulfilled]: publishListingsFulfilled,
  [actions.listings.publishListings.pending]: publishListingsPending,
  [actions.listings.publishListings.rejected]: publishListingsRejected,
  [actions.listings.schedulePublish]: schedulePublish,
  [actions.listings.schedulePublish.fulfilled]: schedulePublishFulfilled,
  [actions.listings.schedulePublish.rejected]: schedulePublishRejected,
  [actions.listings.setData]: setData,
  [actions.listings.setFilter]: setFilter,
  [actions.listings.setModal]: setModal,
  [actions.listings.setOrder]: setOrder,
  [actions.listings.setPage]: setPage,
  [actions.listings.setStatus]: setStatus,
  [actions.listings.toggleListings]: toggleListings,
  [actions.listings.toggleListings.fulfilled]: toggleListingsFulfilled,
  [actions.listings.toggleListings.rejected]: toggleListingsRejected,
  [actions.listings.toggleSidebarGroup]: toggleSidebarGroup,
  [actions.listings.toggleState]: toggleState,
};

export default listings;
